import { msg } from '@lingui/core/macro'
import type { StatusLightToken } from '@oranje/ui'

import { labelMap } from '@/shared/lib/i18nLabels'

/**
 * Estado de revisión del DÍA. Se DERIVA de `operations.timesheet_day`:
 * `has_anomaly` sin resolver → Observado; `review_note` escrita → Revisado;
 * lo demás → Sin revisar. No es columna propia: es la lectura de esas dos.
 *
 * «Pendiente» se descartó para este estado (Hugo, 2026-10-12): la semana
 * tiene su PROPIO «pendiente de aprobación» (otro semáforo, abajo), y las
 * dos palabras iguales en la misma pantalla leían como si un día "pendiente"
 * bloqueara la semana ya "Aprobada" — no es así, son dos cosas que no se
 * tocan. "Sin revisar" no se confunde con eso.
 */
export const TIMESHEET_STATUSES = ['PENDING', 'OBSERVED', 'REVIEWED'] as const

export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number]

const TIMESHEET_STATUS_MESSAGE = {
  PENDING: msg`Sin revisar`,
  OBSERVED: msg`Observado`,
  REVIEWED: msg`Revisado`,
}

export const TIMESHEET_STATUS_LABEL: Record<TimesheetStatus, string> =
  labelMap(TIMESHEET_STATUS_MESSAGE)

export const TIMESHEET_STATUS_TOKEN: Record<TimesheetStatus, StatusLightToken> = {
  PENDING: 'st-azul-claro',
  OBSERVED: 'st-amarillo',
  REVIEWED: 'st-morado',
}

/**
 * Estado de las checadas del día. Es OTRA cosa que el indicador de arriba: uno
 * dice si la persona marcó entrada y salida; el otro, si alguien ya revisó lo
 * que marcó.
 */
/**
 * Estado de la SEMANA (`operations.timesheet.status`, `ck_timesheet_status`):
 * el ciclo revisar → enviar → aprobar de D-09. Es OTRA cosa que el estado del
 * día de arriba, que habla de la revisión de una jornada.
 */
export const TIMESHEET_WEEK_STATUSES = ['OPEN', 'PENDING_APPROVAL', 'APPROVED'] as const

export type TimesheetWeekStatus = (typeof TIMESHEET_WEEK_STATUSES)[number]

const TIMESHEET_WEEK_STATUS_MESSAGE = {
  OPEN: msg`Abierta`,
  PENDING_APPROVAL: msg`Enviada a aprobación`,
  APPROVED: msg`Aprobada`,
}

export const TIMESHEET_WEEK_STATUS_LABEL: Record<TimesheetWeekStatus, string> = labelMap(
  TIMESHEET_WEEK_STATUS_MESSAGE,
)

/**
 * Set propio, sin pisar los colores que ya usa el estado del DÍA (azul
 * claro/amarillo/morado) — los dos semáforos conviven en la misma pantalla.
 */
export const TIMESHEET_WEEK_STATUS_TOKEN: Record<TimesheetWeekStatus, StatusLightToken> = {
  OPEN: 'st-gris',
  PENDING_APPROVAL: 'st-naranja',
  APPROVED: 'st-verde',
}

/**
 * Sentinel de FRONT: una fila sintetizada para quien tiene asignación ACTIVA
 * pero ningún timesheet esta semana — nunca ponchó ni un día (Hugo,
 * 2026-10-13). No vive en `operations.timesheet.status` ni se manda jamás
 * como filtro al servidor — por eso vive aparte de `TIMESHEET_WEEK_STATUSES`,
 * que sí es el enum real que entiende `GET /timesheets?status=`.
 */
export const NO_TIMESHEET_STATUS = 'NO_TIMESHEET'

const VIRTUAL_WEEK_STATUS_MESSAGE = {
  [NO_TIMESHEET_STATUS]: msg`Sin Timesheet`,
}

/** Las tres etiquetas reales + el sentinel, para los sitios que pintan
    `weekStatus` sin saber si la fila es real o sintetizada. */
export const WEEK_STATUS_LABEL_WITH_VIRTUAL: Record<string, string> = {
  ...TIMESHEET_WEEK_STATUS_LABEL,
  ...labelMap(VIRTUAL_WEEK_STATUS_MESSAGE),
}

export const WEEK_STATUS_TOKEN_WITH_VIRTUAL: Record<string, StatusLightToken> = {
  ...TIMESHEET_WEEK_STATUS_TOKEN,
  [NO_TIMESHEET_STATUS]: 'st-gris',
}

export const PUNCH_STATES = ['COMPLETE', 'IN_PROGRESS', 'INCOMPLETE', 'NO_SHIFT'] as const

export type PunchState = (typeof PUNCH_STATES)[number]

const PUNCH_STATE_MESSAGE = {
  COMPLETE: msg`Ponches completos`,
  /* HOY con entrada y sin salida todavía no es un problema: la jornada
     sigue. INCOMPLETE es para un día YA PASADO con un extremo faltante —
     ahí sí hay que resolverlo (Hugo, 2026-10-01). */
  IN_PROGRESS: msg`Trabajando ahora`,
  INCOMPLETE: msg`Ponches incompletos`,
  /* NO "Sin turno": el front solo sabe que no hay marcas ese día (0 punches),
     no si había turno programado — eso vive en el Schedule y hoy no se
     cruza contra el Timesheet. Afirmar "sin turno" sería el mismo error que
     ya se corrigió con `isAbsence`: nombrar una causa que nunca se verificó. */
  NO_SHIFT: msg`Sin ponches ese día`,
}

export const PUNCH_STATE_LABEL: Record<PunchState, string> = labelMap(PUNCH_STATE_MESSAGE)

/** Anchos de columna del control de zoom, en píxeles. */
export const COLUMN_WIDTHS = [90, 134, 180] as const

export type ColumnWidth = (typeof COLUMN_WIDTHS)[number]

export const DEFAULT_COLUMN_WIDTH: ColumnWidth = 134
